import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Collapse, Dialog, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, Paper, Popover, Stack, Table,
  TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableProps,
  TableRow, Tooltip, Typography, Skeleton, useMediaQuery, useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MyButton from './MyButton';
import noDataImage from '../assets/images/undraw_Empty_re_opql.png';
import { TableHeader, TableAction } from '../constants/interfaces';
import { FieldLabelMap, HIDDEN_FIELDS } from '../constants/fieldLabels';
import { formatNumberWithSpaces } from '../utils/utils';

/* Clés à NE jamais formater comme nombre (identifiants techniques) */
const isIdKey = (key: string): boolean => key === 'id' || key.endsWith('_id');

/* ── Dimensionnement responsive ──
 * La table n'est jamais comprimée sous la somme des largeurs minimales de ses
 * colonnes : en dessous, le conteneur défile horizontalement (colonne d'actions
 * figée à droite) au lieu d'écraser les en-têtes à une lettre. */
const colMinWidth = <T,>(h: TableHeader<T>): number => {
  if (h.minWidth) return h.minWidth;
  if (typeof h.width === 'number') return h.width;
  if (h.type === 'boolean') return 70;
  return 120;
};

/* Largeur estimée d'une action : icône (IconButton / render) ≈ 42px, bouton texte ≈ 100px. */
const actionWidth = <T,>(a: TableAction<T>): number =>
  a.render || a.type !== undefined ? 42 : 100;

/* Fond opaque des cellules figées (doivent masquer ce qui défile dessous) :
 * équivalents opaques des fonds de ligne (blanc / zébré / survol). */
const STICKY_BG = { even: '#fff', odd: '#fcfcfc', hover: '#f5f5f5' };

interface MyTableProps<T> extends TableProps {
  headers: TableHeader<T>[];
  data: T[];
  actions?: TableAction<T>[];
  defaultPageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  totalRows: number;
  size?: 'small' | 'medium';
  hidePagination?: boolean;
  maxHeight?: number | string;
  /** Ajoute automatiquement un bouton œil qui affiche toutes les données de la ligne dans un dialog */
  showDetails?: boolean;
  /** Map clé→libellé utilisé dans le dialog de détail (depuis fieldLabels.ts) */
  fieldLabels?: FieldLabelMap;
  /** Noms de champs à afficher comme checkbox (pour les booléens venant de MariaDB en 0/1) */
  booleanFields?: string[];
  /** Clé unique pour persister la visibilité des colonnes en localStorage. Si absent, le bouton Colonnes ne s'affiche pas. */
  tableKey?: string;
  /**
   * Page courante (0-based) contrôlée par le parent. Si fournie, MyTable n'utilise plus
   * son état interne pour la page — utile quand la pagination vit dans Redux/filtres et
   * que la table peut être démontée/remontée (sinon l'UI repart à la page 1 alors que
   * l'offset réel pointe ailleurs).
   */
  page?: number;
  /** Taille de page contrôlée par le parent (mêmes raisons que `page`). */
  pageSize?: number;
  /**
   * Ligne de totaux affichée en pied de tableau (sticky), s'étendant sur toute la pagination.
   * Map clé de colonne → contenu déjà formaté. Les colonnes absentes restent vides.
   * MyTable n'effectue aucun calcul : la valeur doit être fournie (somme client ou agrégat SP).
   */
  totals?: Partial<Record<keyof T, React.ReactNode>>;
  /** Libellé de la première cellule de la ligne de total (déf. « Total »). */
  totalsLabel?: string;
  /** Si fourni, la ligne entière devient cliquable (curseur pointer) et déclenche ce callback. Les clics dans la cellule d'actions ne le déclenchent pas. */
  onRowClick?: (row: T) => void;
  /**
   * Chargement en cours : les lignes sont remplacées par `pageSize` lignes-squelettes
   * (en-têtes et pagination conservés) → hauteur stable, pas de saut de page.
   * L'état « Aucune donnée » n'est affiché qu'une fois le chargement terminé.
   */
  loading?: boolean;
}

/* ── Rendu générique d'une valeur quelconque ── */
const renderValue = (val: unknown, isBool = false): React.ReactNode => {
  if (val === null || val === undefined) return <span style={{ color: '#bdbdbd', fontSize: '0.85rem' }}>—</span>;
  if (typeof val === 'boolean' || isBool)
    return <Checkbox checked={!!val} disabled size="small" sx={{ p: 0 }} />;
  if (typeof val === 'object') {
    return (
      <Chip
        label={JSON.stringify(val).slice(0, 60) + (JSON.stringify(val).length > 60 ? '…' : '')}
        size="small"
        variant="outlined"
        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
      />
    );
  }
  return String(val);
};

/* ── Texte avec ellipsis + tooltip UNIQUEMENT si réellement tronqué ── */
const OverflowText: React.FC<{
  text: string;
  align?: 'left' | 'center' | 'right';
}> = ({ text, align }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  // Mesure au survol : tooltip seulement si le contenu déborde réellement.
  const handleEnter = () => {
    const el = ref.current;
    if (el && el.scrollWidth > el.clientWidth) setOpen(true);
  };
  return (
    <Tooltip title={text} open={open} placement="top-start" enterDelay={300} onClose={() => setOpen(false)}>
      <Box
        component="span"
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setOpen(false)}
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(align ? { textAlign: align } : {}),
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

/* ── Section title avec barre colorée ── */
const SectionTitle = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: 'action.hover',
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      mb: 1,
    }}
  >
    <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', fontSize: 16 }}>
      {icon}
    </Box>
    <Typography variant="overline" fontWeight={700} color="text.secondary" lineHeight={1.5}>
      {label}
    </Typography>
  </Box>
);

/* ── Dialog de détail auto-généré ── */
const DetailDialog = <T extends object>({
  row,
  headers,
  open,
  onClose,
  fieldLabels = {},
  booleanFields = [],
}: {
  row: T | null;
  headers: TableHeader<T>[];
  open: boolean;
  onClose: () => void;
  fieldLabels?: FieldLabelMap;
  booleanFields?: string[];
}) => {
  const [metaOpen, setMetaOpen] = useState(false);
  if (!row) return null;

  const META_KEYS = ['created_at', 'updated_at', 'date_creation', 'date_modification'];

  const headerKeys = headers.map(h => String(h.key));
  const allKeys    = Object.keys(row as object);

  const allBoolKeys = new Set([
    ...headers.filter(h => h.type === 'boolean').map(h => String(h.key)),
    ...booleanFields,
  ]);

  const extraKeys = allKeys.filter(k =>
    !headerKeys.includes(k) &&
    !HIDDEN_FIELDS.includes(k) &&
    !META_KEYS.includes(k)
  );

  const metaKeys = META_KEYS.filter(k => k in (row as object));

  const getLabel = (key: string): string => {
    const h = headers.find(h => String(h.key) === key);
    if (h?.label) return h.label;
    if (fieldLabels[key]) return fieldLabels[key];
    return key;
  };

  const renderRow = (key: string, mono = false, isBool = false) => {
    const header = headers.find(h => String(h.key) === key);
    const rawVal = (row as any)[key];
    const isNum = !header?.noFormat && !isBool &&
      (header?.type === 'number' ||
        (typeof rawVal === 'number' && !isIdKey(key)));
    const content = header?.render
      ? header.render(row as T)
      : isNum
        ? formatNumberWithSpaces(rawVal as number)
        : renderValue(rawVal, isBool);
    return (
      <Box
        key={key}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          py: 0.65,
          px: 0.5,
          borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
          '&:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          sx={{
            width: 160,
            flexShrink: 0,
            ...(mono ? { fontFamily: 'monospace', fontSize: '0.75rem' } : {}),
          }}
        >
          {getLabel(key)}
        </Typography>
        <Typography variant="body2" color="text.primary" component="div" sx={{ flex: 1, wordBreak: 'break-word' }}>
          {content}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <VisibilityIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600} fontSize="1rem">
            Détail de l'enregistrement
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {/* Colonnes du tableau (avec labels) */}
        {headerKeys.length > 0 && (
          <Box mb={extraKeys.length > 0 || metaKeys.length > 0 ? 2 : 0}>
            <SectionTitle icon={<InfoOutlinedIcon fontSize="inherit" />} label="Informations principales" />
            {headerKeys.filter(k => !HIDDEN_FIELDS.includes(k)).map(key =>
              renderRow(key, false, allBoolKeys.has(key))
            )}
          </Box>
        )}

        {/* Champs supplémentaires non affichés dans le tableau */}
        {extraKeys.length > 0 && (
          <Box mb={metaKeys.length > 0 ? 2 : 0}>
            <SectionTitle icon={<TuneIcon fontSize="inherit" />} label="Données supplémentaires" />
            {extraKeys.map(key => renderRow(key, !fieldLabels[key], allBoolKeys.has(key)))}
          </Box>
        )}

        {/* Métadonnées — section repliable */}
        {metaKeys.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'action.hover',
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { bgcolor: 'action.selected' },
              }}
              onClick={() => setMetaOpen(p => !p)}
            >
              <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', fontSize: 16 }}>
                <ScheduleIcon fontSize="inherit" />
              </Box>
              <Typography variant="overline" fontWeight={700} color="text.disabled" lineHeight={1.5} sx={{ flex: 1 }}>
                Métadonnées
              </Typography>
              {metaOpen
                ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              }
            </Box>
            <Collapse in={metaOpen}>
              <Box mt={0.5}>
                {metaKeys.map(key => renderRow(key))}
              </Box>
            </Collapse>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ══════════════════════════════════════════════════════════════════ */

// ── Helpers visibilité colonnes (localStorage) ───────────────────────────
const STORAGE_PREFIX = 'mytable.';

// Renvoie null si AUCUNE préférence n'est enregistrée (jamais configuré),
// sinon le tableau sauvegardé (même vide = « tout afficher », choix délibéré).
function loadHiddenKeys(tableKey?: string): string[] | null {
  if (!tableKey) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${tableKey}.hidden`);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

function saveHiddenKeys(tableKey: string, hidden: string[]): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${tableKey}.hidden`, JSON.stringify(hidden));
  } catch { /* quota plein ou navigation privée → silencieux */ }
}

const MyTable = <T extends object>({
  headers,
  data,
  actions = [],
  defaultPageSize = 5,
  onPageChange,
  totalRows,
  size = 'small',
  hidePagination = false,
  maxHeight,
  showDetails = false,
  fieldLabels = {},
  booleanFields = [],
  tableKey,
  page: controlledPage,
  pageSize: controlledPageSize,
  totals,
  totalsLabel = 'Total',
  onRowClick,
  loading = false,
  ...tableProps
}: MyTableProps<T>) => {
  // Page/taille : contrôlées par le parent si les props sont fournies, sinon état interne.
  const [internalPage, setInternalPage]       = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);
  const currentPage = controlledPage ?? internalPage;
  const rowsPerPage = controlledPageSize ?? internalPageSize;
  const [detailRow, setDetailRow]       = useState<T | null>(null);
  const [colsAnchor, setColsAnchor]     = useState<HTMLElement | null>(null);

  // Visibilité des colonnes (clés masquées). PRIORITÉ :
  //  1. préférence utilisateur sauvegardée (même `[]` = « tout afficher ») → ROI, gagne toujours
  //  2. sinon (jamais configuré) → seed `defaultVisible` de la 1re visite
  const [hiddenKeys, setHiddenKeys] = useState<string[]>(() => {
    const stored = loadHiddenKeys(tableKey);
    if (stored !== null) return stored;
    return headers
      .filter(h => h.defaultVisible === false && !h.locked)
      .map(h => String(h.key));
  });

  // Persiste UNIQUEMENT sur action utilisateur (pas au montage) — sinon le seed
  // serait écrit et deviendrait indistinguable d'un choix délibéré.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!tableKey) return;
    if (!didMountRef.current) { didMountRef.current = true; return; }
    saveHiddenKeys(tableKey, hiddenKeys);
  }, [tableKey, hiddenKeys]);

  // Colonnes secondaires masquées automatiquement sur écran réduit (`hideBelow`).
  const theme = useTheme();
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const belowLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isHiddenByScreen = (h: TableHeader<T>): boolean =>
    (h.hideBelow === 'sm' && belowSm) ||
    (h.hideBelow === 'md' && belowMd) ||
    (h.hideBelow === 'lg' && belowLg);

  const visibleHeaders = useMemo(
    () => headers.filter(h => !hiddenKeys.includes(String(h.key)) && !isHiddenByScreen(h)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers, hiddenKeys, belowSm, belowMd, belowLg]
  );

  const hasActions = actions.length > 0 || showDetails;
  const actionsColWidth = hasActions
    ? (showDetails ? 42 : 0) + actions.reduce((s, a) => s + actionWidth(a), 0) + 16
    : 0;
  const tableMinWidth = visibleHeaders.reduce((s, h) => s + colMinWidth(h), 0) + actionsColWidth;

  // Ombre de la colonne d'actions figée : seulement quand du contenu défile dessous.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollShadow = () => {
    const el = scrollRef.current;
    if (el) setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollShadow();
    const ro = new ResizeObserver(updateScrollShadow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tableMinWidth, data.length]);

  const toggleCol = (key: string) => {
    setHiddenKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const showAll  = () => setHiddenKeys([]);
  const hideAll  = () => setHiddenKeys(
    headers.filter(h => !h.locked).map(h => String(h.key))
  );
  const resetCols = () => {
    if (tableKey) localStorage.removeItem(`${STORAGE_PREFIX}${tableKey}.hidden`);
    setHiddenKeys(
      headers.filter(h => h.defaultVisible === false && !h.locked).map(h => String(h.key))
    );
  };

  // Hauteur moyenne réelle d'une ligne, mesurée sur le dernier rendu de données :
  // les squelettes la reprennent exactement (contenu variable : actions, checkbox, chips…).
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const rowHeightRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (loading || !body || body.rows.length === 0) return;
    rowHeightRef.current = body.getBoundingClientRect().height / body.rows.length;
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    if (controlledPage === undefined) setInternalPage(newPage);
    onPageChange?.(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = parseInt(e.target.value, 10);
    if (controlledPageSize === undefined) setInternalPageSize(n);
    if (controlledPage === undefined) setInternalPage(0);
    onPageChange?.(0, n);
  };

  if (data.length === 0 && !loading) {
    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: 280,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component="img"
          src={noDataImage}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: 0.08,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        <Box sx={{ position: 'relative', textAlign: 'center', px: 3 }}>
          <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
            Aucune donnée
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Aucun enregistrement à afficher pour le moment.
          </Typography>
        </Box>
      </Box>
    );
  }

  const displayedData = onPageChange
    ? data
    : data.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  // Nombre de lignes attendues sur la page demandée (dernière page souvent incomplète) ;
  // total inconnu (1er chargement) → une page pleine.
  const skeletonRows = Math.min(rowsPerPage, Math.max(totalRows - currentPage * rowsPerPage, 0)) || rowsPerPage;
  // Avant toute mesure : estimation (ligne avec actions = IconButton small, plus haute)
  const skeletonRowHeight = rowHeightRef.current ?? (hasActions ? 39 : 35);

  // Colonne d'actions figée à droite : reste accessible pendant le défilement horizontal.
  const stickyActionsSx = {
    position: 'sticky' as const,
    right: 0,
    width: actionsColWidth,
    whiteSpace: 'nowrap' as const,
    boxShadow: canScrollRight ? '-8px 0 8px -8px rgba(16,24,40,0.18)' : 'none',
    transition: 'box-shadow 0.15s ease',
  };

  return (
    <>
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {tableKey && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Tooltip title="Colonnes affichées">
              <IconButton size="small" onClick={(e) => setColsAnchor(e.currentTarget)}>
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(colsAnchor)}
              anchorEl={colsAnchor}
              onClose={() => setColsAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ p: 2, minWidth: 240 }}>
                <Typography variant="overline" fontWeight={700} color="text.secondary">Colonnes affichées</Typography>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={0}>
                  {headers.map(h => {
                    const k = String(h.key);
                    const isVisible = !hiddenKeys.includes(k);
                    return (
                      <FormControlLabel
                        key={k}
                        control={
                          <Checkbox
                            size="small"
                            checked={isVisible}
                            disabled={!!h.locked}
                            onChange={() => toggleCol(k)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {h.label}{h.locked ? ' (verrouillée)' : ''}
                            {isVisible && isHiddenByScreen(h) && (
                              <Typography component="span" variant="caption" color="text.disabled"> · masquée sur cet écran</Typography>
                            )}
                          </Typography>
                        }
                      />
                    );
                  })}
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={showAll}>Tout afficher</Button>
                  <Button size="small" onClick={hideAll}>Tout masquer</Button>
                  <Button size="small" onClick={resetCols}>Réinitialiser</Button>
                </Stack>
              </Box>
            </Popover>
          </Box>
        )}
        <TableContainer
          ref={scrollRef}
          onScroll={updateScrollShadow}
          sx={{ width: '100%', overflowX: 'auto', ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {}) }}
        >
          <Table sx={{ width: '100%', minWidth: tableMinWidth, tableLayout: 'fixed' }} size={size} stickyHeader {...tableProps}>
            <TableHead>
              <TableRow>
                {visibleHeaders.map((header, i) => (
                  <TableCell
                    key={`header-${String(header.key)}-${i}`}
                    align={header.align ?? 'left'}
                    sx={{
                      bgcolor: 'grey.50',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      py: 1.25,
                      width: header.width,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <OverflowText text={header.label} align={header.align} />
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell
                    sx={{
                      ...stickyActionsSx,
                      zIndex: 3, // au-dessus des en-têtes stickyHeader (2)
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  />
                )}
              </TableRow>
            </TableHead>

            <TableBody ref={bodyRef}>
              {loading && Array.from({ length: skeletonRows }, (_, index) => (
                <TableRow
                  key={`skeleton-${index}`}
                  sx={{
                    // Hauteur imposée (mesurée) : les cellules ont py 0 et un contenu plus petit
                    height: skeletonRowHeight,
                    bgcolor: index % 2 !== 0 ? 'rgba(0,0,0,0.015)' : 'transparent',
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  {visibleHeaders.map((header, i) => (
                    <TableCell
                      key={`skeleton-cell-${String(header.key)}-${i}`}
                      align={header.align ?? 'left'}
                      sx={{ py: 0, width: header.width }}
                    >
                      <Skeleton
                        variant="rounded"
                        height={12}
                        // Largeurs variées : rendu moins « grille » qu'une largeur uniforme
                        width={`${55 + ((index * 7 + i * 13) % 35)}%`}
                        sx={{ display: 'inline-block', verticalAlign: 'middle' }}
                      />
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell
                      align="right"
                      sx={{
                        ...stickyActionsSx,
                        zIndex: 1,
                        bgcolor: index % 2 !== 0 ? STICKY_BG.odd : STICKY_BG.even,
                        py: 0,
                        pr: 1,
                      }}
                    >
                      <Skeleton variant="circular" width={22} height={22} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!loading && displayedData.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={onRowClick ? () => {
                    // Texte sélectionné (glisser pour copier) : ne pas ouvrir la ligne
                    if (window.getSelection()?.toString()) return;
                    onRowClick(row);
                  } : undefined}
                  sx={{
                    bgcolor: index % 2 !== 0 ? 'rgba(0,0,0,0.015)' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:hover > .mytable-actions': { bgcolor: STICKY_BG.hover },
                    '&:last-child td, &:last-child th': { border: 0 },
                    transition: 'background-color 0.15s ease',
                    ...(onRowClick ? { cursor: 'pointer' } : {}),
                  }}
                >
                  {visibleHeaders.map((header, i) => {
                    const rawVal = row[header.key];
                    const isBool = header.type === 'boolean' || typeof rawVal === 'boolean';
                    // Formatage numérique automatique : type 'number' forcé, sinon toute
                    // valeur number sauf identifiants (id, *_id) et colonnes noFormat.
                    const isNum = !header.noFormat && !isBool &&
                      (header.type === 'number' ||
                        (typeof rawVal === 'number' && !isIdKey(String(header.key))));
                    // Protection globale : les colonnes numériques (align 'right')
                    // ne sont jamais tronquées par défaut. Surchargeable via noTruncate.
                    const noTrunc = header.noTruncate ?? header.align === 'right';
                    const content: React.ReactNode = header.render
                      ? header.render(row)
                      : isBool
                        ? <Checkbox checked={!!row[header.key]} disabled color="primary" size="small" sx={{ p: 0 }} />
                        : isNum
                          ? formatNumberWithSpaces(rawVal as number)
                          : String(rawVal ?? '');
                    // Texte tronquable (chaîne/nombre) → OverflowText (tooltip seulement si coupé)
                    const isText = typeof content === 'string' || typeof content === 'number';
                    return (
                      <TableCell
                        key={`cell-${String(header.key)}-${i}`}
                        align={header.align ?? 'left'}
                        sx={{
                          py: size === 'small' ? 0.85 : 1.25,
                          width: header.width,
                          whiteSpace: 'nowrap',
                          // Toujours contenu dans sa colonne (jamais de débordement sur la voisine).
                          // noTrunc : pas d'OverflowText — la largeur min. doit suffire au contenu.
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {!noTrunc && isText
                          ? <OverflowText text={String(content)} align={header.align} />
                          : content}
                      </TableCell>
                    );
                  })}

                  {hasActions && (
                    <TableCell
                      align="right"
                      className="mytable-actions"
                      sx={{
                        ...stickyActionsSx,
                        zIndex: 1,
                        bgcolor: index % 2 !== 0 ? STICKY_BG.odd : STICKY_BG.even,
                        py: size === 'small' ? 0.5 : 0.85,
                        pr: 1,
                      }}
                      onClick={onRowClick ? (e) => e.stopPropagation() : undefined}
                    >
                      {showDetails && (
                        <Tooltip title="Voir les détails">
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => setDetailRow(row)}
                            sx={{ mr: actions.length > 0 ? 0.5 : 0 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {actions.map((action, i) =>
                        action.hidden && action.hidden(row) ? null : action.render ? (
                          <React.Fragment key={i}>{action.render(row)}</React.Fragment>
                        ) : (
                          <MyButton
                            key={i}
                            color={action.color}
                            actionType={action.type}
                            disabled={action.disabled ? action.disabled(row) : false}
                            loading={action.loading ? action.loading(row) : false}
                            onClick={() => action.callback?.(row)}
                          >
                            {action.label}
                          </MyButton>
                        )
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>

            {totals && (
              <TableFooter
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                }}
              >
                <TableRow>
                  {visibleHeaders.map((header, i) => {
                    const val = totals[header.key];
                    const isFirst = i === 0;
                    return (
                      <TableCell
                        key={`total-${String(header.key)}-${i}`}
                        align={header.align ?? 'left'}
                        sx={{
                          bgcolor: 'grey.100',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          color: 'text.primary',
                          borderTop: '2px solid',
                          borderColor: 'divider',
                          width: header.width,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          py: 1.25,
                        }}
                      >
                        {val ?? (isFirst ? totalsLabel : '')}
                      </TableCell>
                    );
                  })}
                  {hasActions && (
                    <TableCell
                      sx={{ ...stickyActionsSx, zIndex: 3, bgcolor: 'grey.100', borderTop: '2px solid', borderColor: 'divider' }}
                    />
                  )}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>

        {!hidePagination && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <TablePagination
              component="div"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Lignes par page"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} sur ${count !== -1 ? formatNumberWithSpaces(count) : `plus de ${to}`}`}
              getItemAriaLabel={(type) => ({
                first: 'Première page', last: 'Dernière page', next: 'Page suivante', previous: 'Page précédente',
              }[type])}
              showFirstButton
              showLastButton
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: 44,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  px: { xs: 1, sm: 2 },
                },
                // Écran étroit : pas d'espaceur ni de libellé « Lignes par page » → tout tient sur une ligne.
                '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
                '& .MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'block' } },
                '& .MuiTablePagination-input': { mr: { xs: 1, sm: 4 } },
                '& .MuiTablePagination-actions': { ml: { xs: 0.5, sm: 2.5 } },
              }}
            />
          </Box>
        )}
      </Paper>

      <DetailDialog
        row={detailRow}
        headers={headers}
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        fieldLabels={fieldLabels}
        booleanFields={booleanFields}
      />
    </>
  );
};

export default MyTable;
